import React, { useEffect, useState } from 'react';
import { 
  Paper, Box, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Avatar, Pagination, Chip, Skeleton, IconButton, Tooltip, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, CircularProgress,
  Grid, Divider
} from '@mui/material';
import { getData, postData } from '../Webservice/Webservice';
import { FaCheckCircle } from 'react-icons/fa';
import { MdCancel } from 'react-icons/md';
import { Icon } from '@iconify/react';


const PAGE_SIZE = 20;

const statusColor = (status) => {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  return 'warning'; // pending or others
};

const ProductRequestList = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionData, setActionData] = useState({ id: '', status: '', rejectionReason: '' });
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchRequests = async (page = 1) => {
    setLoading(true);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const res = await getData('product/seller-assign-request/list', { page, limit: PAGE_SIZE }, token, false);

      const list = Array.isArray(res?.request) ? res.request : [];
      const metaRes = res?.meta || { page: 1, totalPages: 1, total: list.length };

      setRequests(list);
      setMeta({ page: metaRes.page || page, totalPages: metaRes.totalPages || 1, total: metaRes.total || list.length });
    } catch (err) {
      setRequests([]);
      setMeta({ page: 1, totalPages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(1);
  }, []);

  const handlePageChange = (_evt, pageNum) => {
    fetchRequests(pageNum);
  };

  const updateStatus = (id, status) => {
    setActionData({ id, status, rejectionReason: '' });
    setActionModalOpen(true);
  };

  const handleConfirmAction = async () => {
    setActionSubmitting(true);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const payload = { 
        status: actionData.status,
        requestId: actionData.id
      };
      if (actionData.status === 'rejected') {
        payload.rejectionReason = actionData.rejectionReason || '';
      }
      await postData(`product/seller-assign-approve`, payload, token);
      setActionModalOpen(false);
      fetchRequests(meta.page);
    } catch (e) {
      // Errors are toasted in webservice
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailModalOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const DetailModal = ({ open, onClose, request }) => {
    if (!request) return null;

    const product = request.product_id || {};
    const createdBy = request.createdBy || {};

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Product Information */}
            <DialogTitle sx={{ fontWeight: 700, textAlign: 'center',fontFamily: 'Mulish, sans-serif',fontSize: 24, }}>
            Request Details
          </DialogTitle>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{mb: 2,   fontFamily: 'Mulish, sans-serif' }}>
                Product Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Product Title</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    {product.title || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">UPC</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    {product.upc || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Ownership</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    {product.ownership || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Is Free Product</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    {product.isFree ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Pricing Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Mulish, sans-serif' }}>
                Pricing Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Maximum Retail Price</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    ₹{request.maximumRetailPrice || '0'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">B2C Selling Price</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    ₹{request.b2c_sellingPrice || '0'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">B2B Selling Price</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    ₹{request.b2b_sellingPrice || '0'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">B2C Discount</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    {product.b2c_discountValue || '0'}% ({product.b2c_discountType || 'percentage'})
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">B2B Discount</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    {product.b2b_discountValue || '0'}% ({product.b2b_discountType || 'percentage'})
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Request Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2,fontFamily: 'Mulish, sans-serif' }}>
                Request Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Request ID</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif', wordBreak: 'break-all' }}>
                    {request._id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip
                    label={request.status}
                    color={statusColor(request.status)}
                    size="small"
                    sx={{ fontWeight: 700, textTransform: 'capitalize', fontFamily: 'Mulish, sans-serif' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    {formatDate(request.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Updated At</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    {formatDate(request.updatedAt)}
                  </Typography>
                </Grid>
                {request.rejectionReason && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Rejection Reason</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif', color: 'error.main' }}>
                      {request.rejectionReason}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Seller Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2,  fontFamily: 'Mulish, sans-serif' }}>
                Seller Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Seller Name</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    {createdBy.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Mobile</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    {createdBy.mobile || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Product Images */}
            {product.product_images && product.product_images.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Mulish, sans-serif' }}>
                    Product Images
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {product.product_images.map((image, index) => (
                      <Avatar
                        key={index}
                        variant="rounded"
                        src={image}
                        alt={`${product.title} - Image ${index + 1}`}
                        sx={{ width: 80, height: 80, cursor: 'pointer' }}
                        onClick={() => window.open(image, '_blank')}
                      />
                    ))}
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: 'rgb(255 209 2)',
              color: 'black',
              border: '1px solid black',
              fontFamily: 'Mulish, sans-serif',
              '&:hover': { backgroundColor: 'rgb(255 209 2)' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={2} sx={{ p: 1, boxShadow: 'none', backgroundColor: '#f8fafc' }}>
        <Typography variant="h3" mb={2} component="h1" sx={{
          color: 'text.primary',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily: 'Mulish, sans-serif',
        }}>
          Product Request List
        </Typography>
      </Paper>

      <TableContainer component={Paper} elevation={0} sx={{
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border:'1px solid #d3d3d3de',
      }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{
              '& th': {
                backgroundColor: '#f8f9fa',
                fontWeight: 800,
                py: 2,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#5c636a',
                fontFamily: 'Mulish, sans-serif',
                borderBottom: '2px solid #edeff1',
              }
            }}>
              <TableCell>Image</TableCell>
              <TableCell>Product Title</TableCell>
              <TableCell>Seller Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell><Skeleton variant="circular" width={40} height={40} /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton width={120} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={120} /></TableCell>
                </TableRow>
              ))
            ) : (
              requests.length > 0 ? (
                requests.map((item) => {
                  const imageUrl = item?.product_id?.product_images?.[0] || '';
                  const title = item?.product_id?.title || '-';
                  const sellerName = item?.createdBy?.name || '-';
                  const sellerMobile = item?.createdBy?.mobile || '-';
                  const stat = item?.status || 'pending';

                  return (
                    <TableRow 
                      key={item._id} 
                      hover
                      sx={{ 
                        '&:hover': { backgroundColor: '#f9fafb' },
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <TableCell onClick={() => handleViewDetails(item)} style={{ cursor: 'pointer' }}>
                        <Avatar
                          variant="rounded"
                          src={imageUrl}
                          alt={title}
                          sx={{ 
                            width: 50, 
                            height: 50, 
                            backgroundColor: '#f3f4f6',
                            borderRadius: '10px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            border: '1px solid #e5e7eb'
                          }}
                        />
                      </TableCell>
                      <TableCell 
                        onClick={() => handleViewDetails(item)} 
                        style={{ cursor: 'pointer' }}
                      >
                        <Typography 
                          fontWeight={700} 
                          sx={{
                            fontFamily:'Mulish, sans-serif',
                            fontSize: '0.95rem',
                            color: '#1a1c1e',
                            '&:hover': { color: '#0d47a1' }
                          }}
                        >
                          {title}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{
                            fontFamily:'Mulish, sans-serif',
                            color: '#6b7280',
                            display: 'inline-block',
                            backgroundColor: '#f3f4f6',
                            px: 1,
                            borderRadius: '4px',
                            mt: 0.5,
                            fontWeight: 600
                          }}
                        >
                          UPC: {item?.product_id?.upc || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleViewDetails(item)} 
                        style={{ cursor: 'pointer' }}
                      >
                        <Typography 
                          fontWeight={500} 
                          sx={{fontFamily:'Mulish, sans-serif', color: '#4b5563', fontSize: '0.9rem'}}
                        >
                          {sellerName}
                        </Typography>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleViewDetails(item)} 
                        style={{ cursor: 'pointer' }}
                      >
                        <Typography 
                          fontWeight={500} 
                          sx={{fontFamily:'Mulish, sans-serif', color: '#6b7280', fontSize: '0.9rem'}}
                        >
                          {sellerMobile}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={() => handleViewDetails(item)} style={{ cursor: 'pointer' }}>
                        <Chip
                          label={stat}
                          size="small"
                          sx={{ 
                            fontFamily:'Mulish, sans-serif',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            borderRadius: '6px',
                            backgroundColor: stat === 'approved' ? '#def7ec' : stat === 'rejected' ? '#fde8e8' : '#fef3c7',
                            color: stat === 'approved' ? '#03543f' : stat === 'rejected' ? '#9b1c1c' : '#92400e',
                            border: 'none'
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Tooltip title="Approve Request">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => updateStatus(item._id, 'approved')}
                                disabled={stat !== 'pending'}
                                sx={{ 
                                  color: stat === 'pending' ? '#059669' : '#9ca3af',
                                  backgroundColor: stat === 'pending' ? 'rgba(5, 150, 105, 0.1)' : 'transparent',
                                  '&:hover': { backgroundColor: 'rgba(5, 150, 105, 0.2)' },
                                  borderRadius: '8px'
                                }}
                              >
                                <Icon icon="charm:shield-tick" width="22" height="22" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Reject Request">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => updateStatus(item._id, 'rejected')}
                                disabled={stat !== 'pending'}
                                sx={{ 
                                  color: stat === 'pending' ? '#dc2626' : '#9ca3af',
                                  backgroundColor: stat === 'pending' ? 'rgba(220, 38, 38, 0.1)' : 'transparent',
                                  '&:hover': { backgroundColor: 'rgba(220, 38, 38, 0.2)' },
                                  borderRadius: '8px'
                                }}
                              >
                                <Icon icon="charm:shield-cross" width="22" height="22" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <img
                          src="./img/nodata.png"
                          alt="nodata"
                          style={{ width: '200px', marginBottom: '16px' }}
                          />
                        <Typography variant="h6" color="textSecondary" sx={{ fontFamily: 'Mulish, sans-serif' }}>No data found</Typography>
                        </Box>
                        </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPagination-ul': { flexWrap: 'nowrap' } }}>
        <Pagination
          count={meta.totalPages}
          page={meta.page}
          onChange={handlePageChange}
          sx={{
            '& .MuiPaginationItem-root': {
                fontFamily: 'Mulish, sans-serif',
                fontWeight: 600,
                borderRadius: '8px',
                '&.Mui-selected': {
                    backgroundColor: 'rgb(255, 209, 2)',
                    color: 'black',
                    '&:hover': {
                        backgroundColor: 'rgb(255, 190, 0)',
                    }
                }
            }
          }}
          shape="rounded"
          showFirstButton
          showLastButton
        />
      </Box>
      {meta.total > 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
          Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
        </Typography>
      )}

      {/* Action Modal */}
      <Dialog open={actionModalOpen} onClose={() => setActionModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>Update Request</DialogTitle>
        <DialogContent>
          <TextField
            label="Request ID"
            value={actionData.id}
            fullWidth
            disabled
            sx={{ mb: 2 , mt : 2}}
          />
          <TextField
            label="Status"
            value={actionData.status}
            fullWidth
            disabled
            sx={{ mb: 2, textTransform: 'capitalize' }}
          />
          {actionData.status === 'rejected' && (
            <TextField
              label="Rejection Reason"
              placeholder="Enter reason (optional)"
              value={actionData.rejectionReason}
              onChange={(e) => setActionData(prev => ({ ...prev, rejectionReason: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setActionModalOpen(false);
              setActionData({ id: '', status: '', rejectionReason: '' });
            }}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            disabled={actionSubmitting}
            sx={{
              minWidth: '120px',
              backgroundColor: 'rgb(255 209 2)',
              color: 'black',
              border: '1px solid black',
              fontFamily: 'Mulish, sans-serif',
              '&:hover': { backgroundColor: 'rgb(255 209 2)' }
            }}
          >
            {actionSubmitting ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail View Modal */}
      <DetailModal 
        open={detailModalOpen} 
        onClose={() => setDetailModalOpen(false)} 
        request={selectedRequest} 
      />
    </Box>
  );
};

export default ProductRequestList;